import axios from "axios";
import {GroupSessionResponse} from "../models/GroupSessionResponse";
import {GroupSessionResultResponse} from "../models/GroupSessionResultResponse";
import {GroupSessionPreferenceResponse} from "../models/GroupSessionPreferenceResponse";
import {getAuthHeaders} from "../utils/auth_utils";

import {API_BASE_URL} from "../config/api";

export class GroupSessionService {
    static async createSession(
        name: string,
        departureAirport: string,
        inviteEmails: string[] = []
    ): Promise<GroupSessionResponse> {
        const headers = await getAuthHeaders();
        const response = await axios.post<GroupSessionResponse>(
            `${API_BASE_URL}/group-session/create`,
            {
                name,
                departureAirport,
                inviteEmails,
            },
            {headers}
        );
        return response.data;
    }

    static async listSessions(): Promise<GroupSessionResponse[]> {
        const headers = await getAuthHeaders();
        const response = await axios.get<GroupSessionResponse[]>(
            `${API_BASE_URL}/group-session`,
            {headers}
        );
        return response.data;
    }

    static async getSession(sessionId: string): Promise<GroupSessionResponse> {
        const headers = await getAuthHeaders();
        const response = await axios.get<GroupSessionResponse>(
            `${API_BASE_URL}/group-session/${sessionId}`,
            {headers}
        );
        return response.data;
    }

    static async invite(sessionId: string, email: string): Promise<void> {
        const headers = await getAuthHeaders();
        await axios.post(
            `${API_BASE_URL}/group-session/${sessionId}/invite`,
            {email},
            {headers}
        );
    }

    static async removeInvite(sessionId: string, email: string): Promise<void> {
        const headers = await getAuthHeaders();
        await axios.delete(
            `${API_BASE_URL}/group-session/${sessionId}/invite/${encodeURIComponent(
                email
            )}`,
            {headers}
        );
    }

    static async join(sessionId: string): Promise<void> {
        const headers = await getAuthHeaders();
        await axios.post(`${API_BASE_URL}/group-session/${sessionId}/join`, {}, {headers});
    }

    static async declineInvitation(sessionId: string): Promise<void> {
        const headers = await getAuthHeaders();
        await axios.post(`${API_BASE_URL}/group-session/${sessionId}/decline`, {}, {headers});
    }

    static async leave(sessionId: string): Promise<void> {
        const headers = await getAuthHeaders();
        await axios.post(`${API_BASE_URL}/group-session/${sessionId}/leave`, {}, {headers});
    }

    static async deleteSession(sessionId: string): Promise<void> {
        const headers = await getAuthHeaders();
        await axios.delete(`${API_BASE_URL}/group-session/${sessionId}`, {headers});
    }

    static async getSessionStatus(sessionId: string): Promise<any[]> {
        const headers = await getAuthHeaders();
        const response = await axios.get<any[]>(
            `${API_BASE_URL}/group-session/${sessionId}/status`,
            {headers}
        );
        return response.data;
    }


    static async submitPreferences(
        sessionId: string,
        payload: any
    ): Promise<GroupSessionPreferenceResponse> {
        const headers = await getAuthHeaders();
        const response = await axios.post<GroupSessionPreferenceResponse>(
            `${API_BASE_URL}/group-session/${sessionId}/preferences`,
            payload,
            {headers}
        );
        return response.data;
    }

    static async getPreferences(
        sessionId: string
    ): Promise<GroupSessionPreferenceResponse[]> {
        const headers = await getAuthHeaders();
        const response = await axios.get<GroupSessionPreferenceResponse[]>(
            `${API_BASE_URL}/group-session/${sessionId}/preferences`,
            {headers}
        );
        return response.data;
    }

    static async submitFinal(sessionId: string): Promise<void> {
        const headers = await getAuthHeaders();
        await axios.post(
            `${API_BASE_URL}/group-session/${sessionId}/submit`,
            {},
            {headers}
        );
    }

    static async computeResults(sessionId: string): Promise<void> {
        const headers = await getAuthHeaders();
        await axios.post(
            `${API_BASE_URL}/group-session/${sessionId}/compute`,
            {},
            {headers}
        );
    }

    static async getResults(
        sessionId: string
    ): Promise<GroupSessionResultResponse[]> {
        const headers = await getAuthHeaders();
        const response = await axios.get<GroupSessionResultResponse[]>(
            `${API_BASE_URL}/group-session/${sessionId}/result`,
            {headers}
        );
        return response.data;
    }
}
